import { useState } from 'react';
import { NewNumericInput } from '../../inputs/NewNumericInput';
import { ModelInputProps } from './ModelInput';
import { convertToNumber } from '../../../../../utils';

type Mutator = (v: any) => any;
const defaultMutator: Mutator = (v) => v;
type MutatorFunction = (m: Mutator) => Mutator;

type ModelNewNumericInputProps = ModelInputProps & {
    integer?: boolean,
    decimalPlaces?: number,
    isFixedDecimal?: boolean,
    noGroupingSeperator?: boolean,
    signed?: boolean,
    maxCharacters?: number,
    max?: number,
    min?: number,
}

function Mutate(mutators: MutatorFunction[]) {
  return mutators?.reduce((prev, current) => current(prev), defaultMutator);
}

export function ModelNewNumericInput({
  integer = false,
  decimalPlaces = 2,
  noGroupingSeperator = false,
  isFixedDecimal = false,
  onFocus,
  onBlur,
  signed = true,
  maxCharacters,
  max,
  min,
  ...props
}: ModelNewNumericInputProps) {
  const [isTyping, setIsTyping] = useState(false);

  const mutateFixedDecimal = (mutator: Mutator = defaultMutator) => ((v: any) => {
    if (isTyping || integer) {
      return mutator(v);
    }
    const valueAsNumber = Number(convertToNumber(mutator(v)));
    if (v == null || v === '' || Number.isNaN(valueAsNumber)) {
      return mutator(v);
    }
    return valueAsNumber.toFixed(decimalPlaces);
  });

  const mutateDecimal = (mutator: Mutator = defaultMutator) => ((v: any) => {
    const str = mutator(v)?.toString()
      ?.replace(/[^0-9.]/g, '')
      ?.replace(/\.{2,}/g, '.');

    const parts = str?.split('.');
    return `${(parts?.[0] ?? '')}${v?.toString()?.includes('.') ? '.' : ''}${(parts?.[1] ?? '')?.substring(0, decimalPlaces)}`;
  });

  const mutateInteger = (mutator: Mutator = defaultMutator) => ((v: any) => (
    mutator(v)?.toString()?.replace(/\D/g, '')
  ));

  const mutateMaxCharacters = (mutator: Mutator = defaultMutator) => ((v: any) => {
    const value = mutator(v)?.toString();
    return value.slice(
      0,
      (maxCharacters ?? 0)
      + (value.includes('-') ? 1 : 0)
      + (value.includes('.') ? ((value?.split('.')?.[1]?.length ?? 0) + 1) : 0),
    );
  });

  const mutateSigned = (mutator: Mutator = defaultMutator) => ((v: any) => (
    `${v?.toString()?.charAt(0) === '-' ? '-' : ''}${mutator(v)}`
  ));

  // Force max to be Int32 maximum value for integers
  const maxValue = integer ? Math.min(2147483647, max ?? 2147483647) : max;

  const mutateMax = (mutator: Mutator = defaultMutator) => ((v: any) => {
    const str = mutator(v);
    const parts = str?.toString()?.split('.');

    return (Number(str) > (maxValue ?? 0))
      ? maxValue?.toString()
      : `${parts?.[0] ?? ''}${str?.includes('.') ? '.' : ''}${parts?.[1] ?? ''}`;
  });

  const mutateMin = (mutator: Mutator = defaultMutator) => ((v: any) => {
    const str = mutator(v);
    const parts = str?.toString()?.split('.');
    const strCount = Number(parts?.[0] ?? 0)?.toString()?.length;
    const minCount = min?.toString()?.length ?? 0;

    if (minCount + 1 > strCount) return str;

    return (Number(str) < (min ?? 0))
      ? min?.toString()
      : `${parts?.[0] ?? ''}${str?.includes('.') ? '.' : ''}${parts?.[1] ?? ''}`;
  });

  const mutateGroupingSeperator = (mutator: Mutator = defaultMutator) => ((v:any) => (
    mutator(v)?.toString()?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
  ));

  const mutateFunction = Mutate([
    ...(integer ? [mutateInteger] : [mutateDecimal]),
    ...(maxCharacters ? [mutateMaxCharacters] : []),
    ...(min ? [mutateMin] : []),
    ...(maxValue ? [mutateMax] : []),
    ...(signed ? [mutateSigned] : []),
    ...(isFixedDecimal ? [mutateFixedDecimal] : []),
    ...(noGroupingSeperator ? [] : [mutateGroupingSeperator]),
  ]);

  const handleFocus = () => {
    setIsTyping(true);
    onFocus?.();
  };

  const handleBlur = (targetValue: string) => {
    setIsTyping(false);
    onBlur?.(targetValue);
  };

  return (
    <NewNumericInput
      {...props}
      onFocus={handleFocus}
      onBlur={handleBlur}
      mutateValue={mutateFunction}
    >
      {props?.children}
    </NewNumericInput>
  );
}
